import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const AdditionalFeatures = () => {
  const features = [
    {
      icon: "Zap",
      title: "Fast POS System",
      description: "Lightning-fast checkout process with barcode scanning, quick product search, and instant receipt printing for seamless customer experience."
    },
    {
      icon: "Wallet",
      title: "Payment Integration",
      description: "Accept multiple payment methods including cash, cards, mobile money, and bank transfers with automatic reconciliation."
    },
    {
      icon: "Gift",
      title: "Customer Loyalty Program",
      description: "Build customer relationships with points-based rewards, special discounts, and personalized promotions to drive repeat business."
    },
    {
      icon: "Database",
      title: "Local Data Backup",
      description: "Automatic local backups ensure your data is safe even during internet outages with easy restoration capabilities."
    },
    {
      icon: "RefreshCcw",
      title: "Manual Cloud Sync",
      description: "Control when to sync your offline data to the cloud, perfect for areas with intermittent internet connectivity."
    },
    {
      icon: "Printer",
      title: "Receipt Printing",
      description: "Professional receipt printing with customizable templates, logo integration, and support for thermal printers."
    }
  ];

  // Add requested features
  const requested = [
    { icon: 'CreditCard', title: 'Online & Offline Payments', description: 'Accept payments online and offline, with reconciliation and Paystack integration.' },
    { icon: 'Percent', title: 'Tax / VAT System', description: 'Flexible tax and VAT settings per product or globally, with automatic calculation.' },
    { icon: 'Truck', title: 'Shipment & Fees', description: 'Configure shipping methods, zones, and per-order fees, and present options at checkout.' },
    { icon: 'Package', title: 'Order Tracking', description: 'Track order status from placement to delivery with customer notifications.' },
    { icon: 'Codesandbox', title: 'Barcode & QR Code', description: 'Generate barcodes and QR codes for products and receipts.' },
    { icon: 'Aperture', title: 'Auto Product Description', description: 'AI-assisted product description generator to speed up catalog creation.' },
    { icon: 'Image', title: 'Unlimited Product Images', description: 'Upload multiple images per product with gallery view.' },
    { icon: 'Upload', title: 'Import from Excel', description: 'Upload product lists via Excel/CSV to populate your catalog quickly.' },
    { icon: 'Repeat', title: 'Migrate from Bunpa', description: 'One-click import tool to move data from Bunpa into Cultaz.' },
    { icon: 'Users', title: 'Customer Management', description: 'Manage customer profiles, purchase history, and notes.' },
    { icon: 'Facebook', title: 'Facebook Catalogue', description: 'Sync your product catalogue to Facebook for ads and shops.' },
    { icon: 'Send', title: 'Telegram Automation', description: 'Automate notifications and order alerts via Telegram.' },
    { icon: 'UserPlus', title: 'Staff Management', description: 'Create staff accounts, roles, and permissions for safe access control.' }
  ];

  return (
    <section className="py-16 md:py-20 lg:py-24 bg-muted/30">
      <div className="container mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-12 md:mb-16"
        >
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-primary/10 mb-4 md:mb-6">
            <Icon name="Sparkles" size={18} color="var(--color-primary)" strokeWidth={2} />
            <span className="ml-2 font-body text-sm font-medium text-primary">More Features</span>
          </div>
          
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-4 md:mb-6">
            Everything You Need to Succeed
          </h2>
          
          <p className="font-body text-base md:text-lg text-muted-foreground">
            Discover additional powerful features designed to streamline your retail operations and enhance customer satisfaction.
          </p>
        </motion.div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {[...features, ...requested].map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              whileHover={{ y: -4 }}
              className="bg-card rounded-xl p-6 md:p-8 border border-border shadow-purple hover:shadow-purple-md transition-smooth"
            >
              <div className="w-12 h-12 md:w-14 md:h-14 rounded-lg bg-primary/10 flex items-center justify-center mb-4 md:mb-6">
                <Icon name={feature?.icon} size={24} color="var(--color-primary)" strokeWidth={2} />
              </div>
              
              <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground mb-2 md:mb-3">
                {feature?.title}
              </h3>
              
              <p className="font-body text-sm md:text-base text-muted-foreground">
                {feature?.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AdditionalFeatures;